import type { MouseEvent } from 'react';
import { cn } from '../../lib/cn';
import type { MobileNavProps } from './mobile-nav.types';

function handleNavigate(
  event: MouseEvent<HTMLAnchorElement>,
  path: string,
  onNavigate: MobileNavProps['onNavigate'],
) {
  if (!onNavigate) return;
  event.preventDefault();
  onNavigate(path);
}

function normalizeProgress(progress: number | undefined) {
  if (typeof progress !== 'number' || Number.isNaN(progress)) return 0;
  return Math.min(100, Math.max(0, progress));
}

export function MobileNav({
  items,
  ariaLabel = 'Mobile navigation',
  onNavigate,
  scrollProgress,
  scrollProgressLabel = 'Page scroll progress',
}: MobileNavProps) {
  const progress = normalizeProgress(scrollProgress);

  return (
    <nav
      aria-label={ariaLabel}
      className="fixed bottom-0 left-0 right-0 z-50 bg-[var(--a-surface)] lg:hidden"
    >
      <progress
        aria-label={scrollProgressLabel}
        max={100}
        value={progress}
        className="pointer-events-none absolute left-0 top-0 h-[2px] w-full appearance-none overflow-hidden border-0 bg-[var(--a-line)] [&::-moz-progress-bar]:bg-[var(--a-primary)] [&::-webkit-progress-bar]:bg-[var(--a-line)] [&::-webkit-progress-value]:bg-[var(--a-primary)]"
      />
      <div className="flex items-center justify-around">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <a
              key={`${item.path}:${item.label}`}
              href={item.path}
              aria-current={item.active ? 'page' : undefined}
              onClick={(event) => handleNavigate(event, item.path, onNavigate)}
              className={cn(
                'flex flex-1 flex-col items-center gap-0.5 py-2 text-xs transition-colors',
                item.active
                  ? 'text-[var(--a-primary-text)]'
                  : 'text-[var(--a-text-faint)] hover:text-[var(--a-text)]',
              )}
            >
              <Icon className="h-5 w-5" strokeWidth={2.25} />
              <span className="font-medium">{item.label}</span>
            </a>
          );
        })}
      </div>
    </nav>
  );
}
